import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { IOAuthLinkSystemJob } from '../../extViewmodels/sdk/models/Ads';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateMetaAdAccounts } from './useGetMetaAdAccounts';
import { invalidateMetaAdsProvider } from './useGetMetaAdsProvider';
import { invalidateMetaPages } from './useGetMetaPages';

/** Why a reconnect failed, and whether the connection had already been disconnected when it did. */
export class ReconnectMetaLeadsError extends Error {
	/**
	 * Wraps the failed step's result.
	 * @param disconnected True when the disconnect went through and only starting the new OAuth session failed.
	 * @param error The failed OperationResult from whichever step failed.
	 */
	public constructor(
		public readonly disconnected: boolean,
		public readonly error: Api.IOperationResultNoValue
	) {
		super(error?.systemMessage ?? 'Failed to reconnect Meta Ads');
	}
}

/**
 * Disconnects Meta Ads and then requests a new OAuth URL, as one mutation. The API refuses a new OAuth session while
 * the connection is enabled, and it stays enabled while Meta rejects its token, so a reconnect has to disconnect first.
 * Disconnecting clears the ad account and page selections. Any failure invalidates the Meta Ads queries, because a
 * disconnect can succeed on the server even when its response never arrives.
 * @param options.impersonationContext The impersonated account, when an admin is acting for a customer.
 * @param options.onError Called with the failed step's result and whether the connection is already disconnected.
 * @param options.onSuccess Called with the OAuth link to send the customer to.
 */
export const useReconnectMetaLeads = ({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (err: ReconnectMetaLeadsError) => void;
	onSuccess?: (value: IOAuthLinkSystemJob) => void;
}) => {
	const userSession = useUserSession();

	return useMutation<IOAuthLinkSystemJob, ReconnectMetaLeadsError>({
		mutationFn: async () => {
			try {
				await userSession.webServiceHelper.callAsync(
					Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'ads/providers/meta/disconnect' }),
					'POST',
					null
				);
			} catch (error) {
				throw new ReconnectMetaLeadsError(false, error as Api.IOperationResultNoValue);
			}
			try {
				return await userSession.webServiceHelper.callAsync<IOAuthLinkSystemJob>(
					Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: 'ads/providers/meta/authUrl' }),
					'POST',
					null
				);
			} catch (error) {
				throw new ReconnectMetaLeadsError(true, error as Api.IOperationResultNoValue);
			}
		},
		onError: error => {
			invalidateMetaAdsProvider({ impersonationContext });
			invalidateMetaAdAccounts({ impersonationContext });
			invalidateMetaPages({ impersonationContext });
			onError?.(error);
		},
		onSuccess,
	});
};
